import { useCallback, useEffect, useState } from 'react'

import { TagFragmentDoc, useUpdateFeedbackPostTagsMutation } from '~generated/hooks'
import { TagFragment } from '~generated/operations'
import { tagObjTypename } from '~lib/apollo/typenames'
import useDebounce from '~lib/hooks/useDebounce'

interface UpdateFeedbackPostTagsOptions {
  id: string
  tags: TagFragment[]
  updatedAt: number
}

export default function useUpdateFeedbackPostTags({ id, tags, updatedAt }: UpdateFeedbackPostTagsOptions) {
  const [updateFeedbackPostTagsMutation, { client }] = useUpdateFeedbackPostTagsMutation()

  const [state, setState] = useState({ tags: tags || [], updatedAt })

  useEffect(() => {
    if (!state.updatedAt || updatedAt > state.updatedAt) {
      setState({ tags, updatedAt })
    }
  }, [tags, updatedAt, state.updatedAt])

  const handleDebouncedUpdate = useDebounce(
    (tagIds: string[]) => {
      updateFeedbackPostTagsMutation({
        variables: {
          id,
          tagIds,
        },
        update: (_, { data }) => {
          setState((prev) => ({
            ...prev,
            updatedAt: data.updateFeedbackPostAsAdmin.updatedAt,
          }))
        },
      })
    },
    500,
    [id]
  )

  const handleUpdateFeedbackPostTags = useCallback(
    (tagIds: string[]) => {
      const tags = tagIds.map((id) => {
        return client.readFragment<TagFragment>({
          id: client.cache.identify({ id, __typename: tagObjTypename }),
          fragment: TagFragmentDoc,
        })
      })

      setState({ tags, updatedAt: Date.now() })
      handleDebouncedUpdate(tagIds)
    },
    [client, handleDebouncedUpdate]
  )

  return [state.tags, handleUpdateFeedbackPostTags] as const
}
